"use client";

import { formatDobInput } from "@/app/directories/companion/components_v2/Inputs";
import { Input } from "@/shared/src";
import { getOrgById } from "@frontend/lib/actions/actions";
import {
  EVENT_CATEGORIES,
  EVENT_CONTEXTS,
  trackEvent,
} from "@frontend/lib/analytics";
import {
  AppointmentStatusEnum,
  InsurancesStatusEnum,
  ReviewStatusEnum,
  type SlotOrderByWithRelationInput,
  type SlotWhereInput,
  SortOrder,
} from "@frontend/lib/graphql/generated-gql";
import type { GetProvidersByOrgIdProvider as Provider } from "@frontend/lib/graphql/gql-types";
import { Button } from "@frontend/shared/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandItem,
  CommandList,
} from "@frontend/shared/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@frontend/shared/components/ui/dropdown-menu";
import { Label } from "@frontend/shared/components/ui/label";
import { cn } from "@frontend/shared/lib/utils";
import { SlotReviewStatusEnum } from "@/app/common/constants";
import { useQuery } from "@tanstack/react-query";
import { Command as CommandPrimitive } from "cmdk";
import { addDays, format, isValid, parse, startOfDay } from "date-fns";
import {
  Check,
  ChevronRight,
  Eye,
  EyeOff,
  Filter,
  MoveLeft,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";

export type FilterOptionType<GqlType = unknown> = {
  gql: GqlType | string | undefined;
  menuLabel: string;
  tagLabel: string;
};

export type FilterType = {
  orderBy: FilterOptionType<SlotOrderByWithRelationInput>;
  date?: FilterOptionType<SlotWhereInput>;
  insuranceStatus?: FilterOptionType<InsurancesStatusEnum>;
  notReady?: FilterOptionType<string | SlotWhereInput>;
  needsReview?: FilterOptionType<SlotWhereInput>;
  provider?: FilterOptionType<SlotWhereInput>;
  slotReviewStatus?: FilterOptionType<SlotWhereInput>;
  hrtFlag?: FilterOptionType<SlotWhereInput>;
  unpaid?: FilterOptionType<SlotWhereInput>;
};

export type FiltersType = Record<AppointmentStatusEnum, FilterType>;

export const sortOptions: Record<
  string,
  FilterOptionType<SlotOrderByWithRelationInput>
> = {
  earliestApptTime: {
    gql: { startTime: SortOrder.Asc },
    menuLabel: "Oldest first",
    tagLabel: "",
  },
  latestApptTime: {
    gql: { startTime: SortOrder.Desc },
    menuLabel: "Newest first",
    tagLabel: "Sort: newest first",
  },
  apptTime: {
    gql: { startTime: SortOrder.Asc },
    menuLabel: "Appointment time",
    tagLabel: "",
  },
  checkinTime: {
    gql: {
      appointment: { checkedInAt: { sort: SortOrder.Asc } },
    },
    menuLabel: "Check in time",
    tagLabel: "Sort: check in time",
  },
};

export const procedureTypeOptions: Record<string, FilterOptionType<string>> = {
  surgery: {
    gql: "isSurgery",
    menuLabel: "Surgery",
    tagLabel: "Surgery",
  },
  annualVisit: {
    gql: "isAnnualVisit",
    menuLabel: "Annual Visit",
    tagLabel: "Annual Visit",
  },
  telehealth: {
    gql: "isVirtualVisit",
    menuLabel: "Telehealth",
    tagLabel: "Telehealth",
  },
  inOffice: {
    gql: "isInOfficeProcedure",
    menuLabel: "In Office Procedure",
    tagLabel: "In Office Procedure",
  },
};

export const dateOptions: Record<
  string,
  (date?: Date) => FilterOptionType<SlotWhereInput>
> = {
  today: () => ({
    gql: {
      startTime: {
        gte: startOfDay(new Date()),
        lt: addDays(startOfDay(new Date()), 1),
      },
    },
    menuLabel: "Today",
    tagLabel: "",
  }),
  tomorrow: () => ({
    gql: {
      startTime: {
        gte: addDays(startOfDay(new Date()), 1),
        lt: addDays(startOfDay(new Date()), 2),
      },
    },
    menuLabel: "Tomorrow",
    tagLabel: "Tomorrow",
  }),
  all: () => ({
    gql: undefined,
    menuLabel: "All dates",
    tagLabel: "Viewing all dates",
  }),
  arbitrary: (date?: Date) => {
    if (!date) {
      throw new Error("Tried to instantiate a date filter without a date");
    }
    return {
      gql: {
        startTime: {
          gte: startOfDay(date),
          lt: addDays(startOfDay(date), 1),
        },
      },
      menuLabel: "Jump to date",
      tagLabel: format(date, "MM/dd/yyyy"),
    };
  },
};

export const insuranceStatusOptions: Record<
  string,
  FilterOptionType<InsurancesStatusEnum>
> = {
  active: {
    gql: InsurancesStatusEnum.Active,
    menuLabel: "Active",
    tagLabel: "Active",
  },
  manuallyVerified: {
    gql: InsurancesStatusEnum.Manuallyverified,
    menuLabel: "Manually Verified",
    tagLabel: "Manually Verified",
  },
  manuallyVerifiedExpired: {
    gql: InsurancesStatusEnum.ManuallyVerifiedExpired,
    menuLabel: "Manually Verified (Expired)",
    tagLabel: "Manually Verified (Expired)",
  },
  activeBenefitsNotReturned: {
    gql: InsurancesStatusEnum.ActiveBenefitsNotReturned,
    menuLabel: "Active (Benefits not Returned)",
    tagLabel: "Active (Benefits not Returned)",
  },
  activeOutOfNetwork: {
    gql: InsurancesStatusEnum.ActiveOutOfNetwork,
    menuLabel: "Active (Out of Network)",
    tagLabel: "Active (Out of Network)",
  },
  inactive: {
    gql: InsurancesStatusEnum.Inactive,
    menuLabel: "Inactive",
    tagLabel: "Inactive",
  },
  unverified: {
    gql: InsurancesStatusEnum.Unverified,
    menuLabel: "Unverified",
    tagLabel: "Unverified",
  },
  all: {
    gql: undefined,
    menuLabel: "",
    tagLabel: "",
  },
};

export const slotReviewStatusOptions: Record<
  string,
  FilterOptionType<SlotWhereInput>
> = {
  needs_update: {
    gql: {
      reviewStatus: { equals: SlotReviewStatusEnum.NeedsReschedule },
    },
    menuLabel: "Needs to Be Rescheduled",
    tagLabel: "Needs to Be Rescheduled",
  },
};

export const notReadyOptions: Record<
  string,
  FilterOptionType<SlotWhereInput>
> = {
  hide: {
    gql: "hideNotReady",
    menuLabel: "Hide not ready",
    tagLabel: "Hiding not ready",
  },
  show: {
    gql: undefined,
    menuLabel: "Show not ready",
    tagLabel: "",
  },
  showIntakeMissing: {
    gql: "showIntakeMissing",
    menuLabel: "Intake completed",
    tagLabel: "Intake completed",
  },
  showUnpaid: {
    gql: "showUnpaid",
    menuLabel: "Unpaid",
    tagLabel: "Unpaid",
  },
};

export const needsReviewOptions: Record<
  string,
  FilterOptionType<SlotWhereInput>
> = {
  needsReview: {
    gql: {
      boughtTreatments: {
        some: {
          reviewStatus: {
            in: [
              ReviewStatusEnum.NeedsAdminCreated,
              ReviewStatusEnum.ConfirmSelfpay,
              ReviewStatusEnum.ConfirmPrimaryOnly,
            ],
          },
        },
      },
    },
    menuLabel: "Needs review",
    tagLabel: "Needs review",
  },
};

// biome-ignore lint/style/useConst: <explanation>
export let defaultFilters: FiltersType = {
  upcoming: {
    orderBy: sortOptions.earliestApptTime,
    date: dateOptions.today(),
    insuranceStatus: insuranceStatusOptions.all,
    notReady: notReadyOptions.show,
  },
  active: {
    orderBy: sortOptions.apptTime,
  },
  missed: {
    orderBy: sortOptions.earliestApptTime,
  },
  canceled: {
    orderBy: sortOptions.earliestApptTime,
  },
  completed: {
    orderBy: sortOptions.earliestApptTime,
  },
  deleted: {
    orderBy: sortOptions.earliestApptTime,
  },
};

type Submenu = keyof FilterType;

const Filters = ({
  tab,
  filter,
  setFilter,
  orgId,
  practiceId,
}: {
  tab: AppointmentStatusEnum;
  filter: FilterType;
  setFilter: (newFilter: Partial<FilterType>) => void;
  orgId: number;
  practiceId: number | undefined;
}) => {
  const [open, setOpen] = useState(false);
  const [submenu, setSubmenu] = useState<Submenu | undefined>(undefined);

  const newDateCanary = startOfDay(new Date());

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (
      JSON.stringify(defaultFilters.upcoming.date) !==
      JSON.stringify(dateOptions.today())
    ) {
      defaultFilters.upcoming.date = dateOptions.today();
    }

    if (
      filter.date?.menuLabel === "Today" &&
      JSON.stringify(filter.date) !== JSON.stringify(dateOptions.today())
    ) {
      setFilter({ date: dateOptions.today() });
    }

    if (
      filter.date?.menuLabel === "Tomorrow" &&
      JSON.stringify(filter.date) !== JSON.stringify(dateOptions.tomorrow())
    ) {
      setFilter({ date: dateOptions.tomorrow() });
    }
  }, [newDateCanary.toISOString(), tab]);

  const { data: orgProviders } = useQuery<Provider[]>({
    queryFn: async () => (await fetch(`/api/providers?orgId=${orgId}`)).json(),
    queryKey: ["getProvidersByOrgId", orgId],
    enabled: practiceId === undefined,
  });

  const { data: practiceProviders } = useQuery<Provider[]>({
    queryFn: async () =>
      (await fetch(`/api/providers?practiceId=${practiceId}`)).json(),
    queryKey: ["getProvidersByPracticeId", practiceId],
    enabled: practiceId !== undefined,
  });

  const { data: orgData } = useQuery({
    queryFn: () => getOrgById(orgId),
    queryKey: ["getOrgById", orgId],
  });

  const isEhrAgnostic = orgData?.schedulingType === "AGNOSTIC";

  const providers = practiceId ? practiceProviders : orgProviders;

  return (
    <DropdownMenu
      open={open}
      onOpenChange={() => {
        setSubmenu(undefined);
        setOpen(!open);
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="icon"
          size="icon"
          className={cn(
            "border shrink-0 h-12 w-12",
            open ? "border-superscriptBlue" : "border-[#E1E1E1]",
          )}
        >
          {open ? <X size={24} /> : <Filter size={24} />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="rounded-[30px] left-0 border border-superscriptBlue px-3 py-2 min-w-60"
      >
        {submenu === "date" && (
          <DateSubmenu
            filter={filter}
            setFilter={setFilter}
            setSubmenu={setSubmenu}
          />
        )}
        {submenu === "insuranceStatus" && (
          <InsuranceStatusSubmenu
            filter={filter}
            setFilter={setFilter}
            setSubmenu={setSubmenu}
          />
        )}
        {submenu === "provider" && !!providers && (
          <ProviderSubmenu
            filter={filter}
            setFilter={setFilter}
            setSubmenu={setSubmenu}
            providers={providers}
          />
        )}
        {submenu === "needsReview" && (
          <ToggleNeedsReviewButton filter={filter} setFilter={setFilter} />
        )}
        {submenu === "slotReviewStatus" && !!providers && isEhrAgnostic && (
          <SlotReviewStatusSubMenu
            filter={filter}
            setFilter={setFilter}
            setSubmenu={setSubmenu}
            providers={providers}
          />
        )}
        {submenu === "hrtFlag" && (
          <ProcedureTypeSubmenu
            filter={filter}
            setFilter={setFilter}
            setSubmenu={setSubmenu}
          />
        )}

        {submenu === undefined && tab === AppointmentStatusEnum.Upcoming && (
          <>
            <DropdownMenuLabel>Filter by</DropdownMenuLabel>
            <SubmenuButton
              filter={filter}
              tab={tab}
              label="Date"
              submenu="date"
              setSubmenu={setSubmenu}
            />
            <SubmenuButton
              filter={filter}
              tab={tab}
              label="Provider"
              submenu="provider"
              setSubmenu={setSubmenu}
            />
            <SubmenuButton
              filter={filter}
              tab={tab}
              label="Insurance status"
              submenu="insuranceStatus"
              setSubmenu={setSubmenu}
            />
            <SubmenuButton
              filter={filter}
              tab={tab}
              label="Procedure type"
              submenu="hrtFlag"
              setSubmenu={setSubmenu}
            />

            {isEhrAgnostic && (
              <SubmenuButton
                filter={filter}
                tab={tab}
                label="Slot status"
                submenu="slotReviewStatus"
                setSubmenu={setSubmenu}
              />
            )}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Show only</DropdownMenuLabel>
            <FilterButton
              filter={filter}
              setFilter={setFilter}
              option={notReadyOptions.showUnpaid}
              setTo="unpaid"
            />
            <FilterButton
              filter={filter}
              setFilter={setFilter}
              option={needsReviewOptions.needsReview}
              setTo="needsReview"
            />
            <ToggleNotReadyButton filter={filter} setFilter={setFilter} />
          </>
        )}
        {submenu === undefined && tab === AppointmentStatusEnum.Active && (
          <>
            <DropdownMenuLabel>Sort By</DropdownMenuLabel>
            <FilterButton
              filter={filter}
              setFilter={setFilter}
              option={sortOptions.checkinTime}
              setTo="orderBy"
            />
            <FilterButton
              filter={filter}
              setFilter={setFilter}
              option={sortOptions.apptTime}
              setTo="orderBy"
            />
            <DropdownMenuSeparator />

            <DropdownMenuLabel>Filter by</DropdownMenuLabel>
            <SubmenuButton
              filter={filter}
              tab={tab}
              label="Provider"
              submenu="provider"
              setSubmenu={setSubmenu}
            />
            <SubmenuButton
              filter={filter}
              tab={tab}
              label="Procedure type"
              submenu="hrtFlag"
              setSubmenu={setSubmenu}
            />
            <SubmenuButton
              filter={filter}
              tab={tab}
              label="Insurance status"
              submenu="insuranceStatus"
              setSubmenu={setSubmenu}
            />
            <DropdownMenuSeparator />

            <DropdownMenuLabel>Show only</DropdownMenuLabel>
            <FilterButton
              filter={filter}
              setFilter={setFilter}
              option={notReadyOptions.showUnpaid}
              setTo="unpaid"
            />
          </>
        )}
        {submenu === undefined && tab === AppointmentStatusEnum.Missed && (
          <>
            <DropdownMenuLabel>Sort By</DropdownMenuLabel>
            <FilterButton
              filter={filter}
              setFilter={setFilter}
              option={sortOptions.earliestApptTime}
              setTo="orderBy"
            />
            <FilterButton
              filter={filter}
              setFilter={setFilter}
              option={sortOptions.latestApptTime}
              setTo="orderBy"
            />
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Filter by</DropdownMenuLabel>
            <SubmenuButton
              filter={filter}
              tab={tab}
              label="Provider"
              submenu="provider"
              setSubmenu={setSubmenu}
            />
            <SubmenuButton
              filter={filter}
              tab={tab}
              label="Procedure type"
              submenu="hrtFlag"
              setSubmenu={setSubmenu}
            />
            <SubmenuButton
              filter={filter}
              tab={tab}
              label="Insurance status"
              submenu="insuranceStatus"
              setSubmenu={setSubmenu}
            />
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Show only</DropdownMenuLabel>
            <FilterButton
              filter={filter}
              setFilter={setFilter}
              option={notReadyOptions.showUnpaid}
              setTo="unpaid"
            />
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ToggleNeedsReviewButton = ({
  filter,
  setFilter,
}: {
  filter: FilterType;
  setFilter: (newFilter: Partial<FilterType>) => void;
}) => {
  const needsReviewHidden = !!filter.needsReview?.gql;

  return needsReviewHidden ? (
    <DropdownMenuItem
      className="flex items-center gap-3 text-sm w-full"
      onClick={() => setFilter({ needsReview: undefined })}
    >
      <EyeOff size={16} /> Needs review?
    </DropdownMenuItem>
  ) : (
    <DropdownMenuItem
      className="flex items-center gap-3 text-sm  w-full"
      onClick={() =>
        setFilter({
          needsReview: needsReviewOptions.needsReview,
        })
      }
    >
      <Eye size={16} /> Needs review?
    </DropdownMenuItem>
  );
};

const FilterButton = ({
  filter,
  setFilter,
  option,
  setTo,
}: {
  filter: FilterType;
  setFilter: (newFilter: Partial<FilterType>) => void;
  option: FilterOptionType;
  setTo: keyof FilterType;
}) => (
  <DropdownMenuItem
    onClick={(e) => {
      e.preventDefault();
      setFilter({ [setTo]: option });
    }}
    className="flex justify-between"
  >
    {option.menuLabel}
    {JSON.stringify(filter[setTo]) === JSON.stringify(option) && (
      <Check color="#00a3ff" size={16} />
    )}
  </DropdownMenuItem>
);

const SubmenuButton = ({
  submenu,
  setSubmenu,
  label,
  filter,
  tab,
}: {
  submenu: Submenu;
  setSubmenu: (newSubmenu: Submenu | undefined) => void;
  label: string;
  filter: FilterType;
  tab: AppointmentStatusEnum;
}) => (
  <DropdownMenuItem
    onClick={(e) => {
      e.preventDefault();
      setSubmenu(submenu);
    }}
    className="flex justify-between items-center"
  >
    {label}
    <div className="flex items-center gap-2">
      {JSON.stringify(filter[submenu]) !==
        JSON.stringify(defaultFilters[tab][submenu]) && (
        <div className="w-2 h-2 m-1 rounded-full bg-superscriptBlue" />
      )}
      <ChevronRight size={16} />
    </div>
  </DropdownMenuItem>
);

const ExitSubmenuButton = ({
  setSubmenu,
}: { setSubmenu: (newValue: Submenu | undefined) => void }) => (
  <DropdownMenuItem
    onClick={(e) => {
      e.preventDefault();
      setSubmenu(undefined);
    }}
    className="mb-4"
  >
    <MoveLeft />
  </DropdownMenuItem>
);

const ProcedureTypeSubmenu = ({
  filter,
  setFilter,
  setSubmenu,
}: {
  filter: FilterType;
  setFilter: (newFilter: Partial<FilterType>) => void;
  setSubmenu: (newValue: Submenu | undefined) => void;
}) => (
  <>
    <ExitSubmenuButton setSubmenu={setSubmenu} />
    <FilterButton
      filter={filter}
      setFilter={setFilter}
      option={procedureTypeOptions.surgery}
      setTo="hrtFlag"
    />
    <FilterButton
      filter={filter}
      setFilter={setFilter}
      option={procedureTypeOptions.annualVisit}
      setTo="hrtFlag"
    />
    <FilterButton
      filter={filter}
      setFilter={setFilter}
      option={procedureTypeOptions.telehealth}
      setTo="hrtFlag"
    />
    <FilterButton
      filter={filter}
      setFilter={setFilter}
      option={procedureTypeOptions.inOffice}
      setTo="hrtFlag"
    />
  </>
);

const DateSubmenu = ({
  filter,
  setFilter,
  setSubmenu,
}: {
  filter: FilterType;
  setFilter: (newFilter: Partial<FilterType>) => void;
  setSubmenu: (newValue: Submenu | undefined) => void;
}) => (
  <>
    <ExitSubmenuButton setSubmenu={setSubmenu} />
    <FilterButton
      filter={filter}
      setFilter={setFilter}
      option={dateOptions.today()}
      setTo="date"
    />
    <FilterButton
      filter={filter}
      setFilter={setFilter}
      option={dateOptions.tomorrow()}
      setTo="date"
    />
    <FilterButton
      filter={filter}
      setFilter={setFilter}
      option={dateOptions.all()}
      setTo="date"
    />
    <ArbitraryDateEntry setFilter={setFilter} />
  </>
);

const ArbitraryDateEntry = ({
  setFilter,
}: {
  setFilter: (newFilter: Partial<FilterType>) => void;
}) => {
  const [dateString, setDateString] = useState("");

  const parsedDate = parse(dateString, "MM/dd/yyyy", new Date());

  const dateIsValid = dateString.length === 10 && isValid(parsedDate);

  return (
    <div className="relative flex justify-between border border-[#6D757F] rounded-full focus-within:border-superscriptBlue mt-2 p-0.5">
      <Input
        id="jumpToDate"
        type="text"
        placeholder="MM/DD/YYYY"
        value={dateString}
        onChange={(e) => setDateString(formatDobInput(e.target.value))}
        className="border-none peer bg-transparent focus-visible:ring-transparent focus-visible:ring-offset-transparent ml-2 w-32"
      />
      {dateIsValid && (
        <Button
          className="rounded-full peer"
          onClick={() => {
            trackEvent({
              category: EVENT_CATEGORIES.ADMIN,
              context: EVENT_CONTEXTS.ROSTER,
              action: "jump_to_date",
            });
            setFilter({
              date: dateOptions.arbitrary(parsedDate),
            });
          }}
        >
          Go
        </Button>
      )}
      <Label
        htmlFor="jumpToDate"
        className="text-[#6D757F] peer-focus:text-superscriptBlue absolute bg-white text-[10px] leading-[1.2] -top-1.5 left-4 px-1 font-light"
      >
        Jump to date
      </Label>
    </div>
  );
};

const InsuranceStatusSubmenu = ({
  filter,
  setFilter,
  setSubmenu,
}: {
  filter: FilterType;
  setFilter: (newFilter: Partial<FilterType>) => void;
  setSubmenu: (newValue: Submenu | undefined) => void;
}) => (
  <>
    <ExitSubmenuButton setSubmenu={setSubmenu} />
    <FilterButton
      filter={filter}
      setFilter={setFilter}
      option={insuranceStatusOptions.active}
      setTo="insuranceStatus"
    />
    <FilterButton
      filter={filter}
      setFilter={setFilter}
      option={insuranceStatusOptions.manuallyVerified}
      setTo="insuranceStatus"
    />
    <FilterButton
      filter={filter}
      setFilter={setFilter}
      option={insuranceStatusOptions.manuallyVerifiedExpired}
      setTo="insuranceStatus"
    />
    <FilterButton
      filter={filter}
      setFilter={setFilter}
      option={insuranceStatusOptions.activeBenefitsNotReturned}
      setTo="insuranceStatus"
    />
    <FilterButton
      filter={filter}
      setFilter={setFilter}
      option={insuranceStatusOptions.activeOutOfNetwork}
      setTo="insuranceStatus"
    />
    <FilterButton
      filter={filter}
      setFilter={setFilter}
      option={insuranceStatusOptions.inactive}
      setTo="insuranceStatus"
    />
    <FilterButton
      filter={filter}
      setFilter={setFilter}
      option={insuranceStatusOptions.unverified}
      setTo="insuranceStatus"
    />
  </>
);

const ToggleNotReadyButton = ({
  filter,
  setFilter,
}: {
  filter: FilterType;
  setFilter: (newFilter: Partial<FilterType>) => void;
}) => {
  const notReadyHidden = !!filter.notReady?.gql;

  return notReadyHidden ? (
    <DropdownMenuItem
      className="flex items-center gap-3 text-sm justify-between"
      onClick={() => setFilter({ notReady: notReadyOptions.show })}
    >
      Ready for checkin
      <Check size={16} color="#00a3ff" />
    </DropdownMenuItem>
  ) : (
    <DropdownMenuItem
      className="flex items-center gap-3 text-sm justify-between"
      onClick={() => setFilter({ notReady: notReadyOptions.hide })}
    >
      Ready for checkin
    </DropdownMenuItem>
  );
};

const ProviderSubmenu = ({
  filter,
  setFilter,
  setSubmenu,
  providers,
}: {
  filter: FilterType;
  setFilter: (newFilter: Partial<FilterType>) => void;
  setSubmenu: (newValue: Submenu | undefined) => void;
  providers: Provider[];
}) => {
  const providerOptions = providers
    .map((p) => ({
      gql: { providerId: { equals: p.id } },
      menuLabel: `${p.firstName} ${p.lastName}`,
      tagLabel: `${p.firstName} ${p.lastName}`,
    }))
    .sort((a, b) => a.menuLabel.localeCompare(b.menuLabel));

  return (
    <>
      <ExitSubmenuButton setSubmenu={setSubmenu} />
      <Command label="Search providers">
        <div className="relative flex justify-between border border-[#6D757F] rounded-full focus-within:border-superscriptBlue mt-2 p-0.5 mb-2.5">
          <CommandPrimitive.Input className="border-none focus:outline-none peer w-40 ml-5 my-1.5" />
          <Label
            htmlFor="searchProviders"
            className="text-[#6D757F] peer-focus:text-superscriptBlue absolute bg-white text-[10px] leading-[1.2] -top-1.5 left-4 px-1 font-light"
          >
            Search providers
          </Label>
        </div>
        <CommandList className="no-scrollbar">
          <CommandEmpty>No results.</CommandEmpty>
          {providerOptions.map((o) => (
            <CommandItem
              key={o.menuLabel}
              onSelect={() => {
                trackEvent({
                  category: EVENT_CATEGORIES.ADMIN,
                  context: EVENT_CONTEXTS.ROSTER,
                  action: "provider_filter",
                });
                setFilter({ provider: o });
              }}
              className="flex justify-between"
            >
              {o.menuLabel}
              {JSON.stringify(filter.provider) === JSON.stringify(o) && (
                <Check color="#00a3ff" size={16} />
              )}
            </CommandItem>
          ))}
        </CommandList>
      </Command>
    </>
  );
};

const SlotReviewStatusSubMenu = ({
  filter,
  setFilter,
  setSubmenu,
  providers,
}: {
  filter: FilterType;
  setFilter: (newFilter: Partial<FilterType>) => void;
  setSubmenu: (newValue: Submenu | undefined) => void;
  providers: Provider[];
}) => {
  return (
    <>
      <ExitSubmenuButton setSubmenu={setSubmenu} />
      <FilterButton
        filter={filter}
        setFilter={setFilter}
        option={slotReviewStatusOptions.needs_update}
        setTo="slotReviewStatus"
      />
    </>
  );
};

export default Filters;
